// Pure read-only queries over GameState: abilities, modifiers, costs, legality, requirements, stats.

import { type ContentIndex, decreeDef, locationDef, problemDef, resolutionDef, spellDef, spellValues } from './content.js';
import {
  ANNEX_IDS,
  COLORS,
  SCHOOLS,
  SCHOOL_COLOR,
  type Ability,
  type Assistant,
  type CardId,
  type Color,
  type Condition,
  type DecreeInForce,
  type DefId,
  type GameState,
  type GlobalMod,
  type Limit,
  type LocationId,
  type Modifier,
  type Payment,
  type Permits,
  type PermitBag,
  type ProblemOption,
  type Requirement,
  type ResolutionEntry,
  type ResolutionMod,
  type Result,
  type RuleError,
  type School,
  type SeatId,
  type SourceRef,
  type StatName,
} from './types.js';

// ─── Permit helpers ──────────────────────────────────────────────────────────

export const emptyPermits = (): Permits => ({ red: 0, yellow: 0, blue: 0, green: 0 });

export function toPermits(bag: PermitBag | undefined): Permits {
  const p = emptyPermits();
  if (bag) for (const c of COLORS) p[c] = bag[c] ?? 0;
  return p;
}

export const permitTotal = (p: PermitBag): number => COLORS.reduce((n, c) => n + (p[c] ?? 0), 0);

export function compactBag(p: PermitBag): PermitBag {
  const out: PermitBag = {};
  for (const c of COLORS) if ((p[c] ?? 0) > 0) out[c] = p[c];
  return out;
}

export const err = (code: RuleError['code'], params?: RuleError['params']): { ok: false; error: RuleError } => ({
  ok: false,
  error: params ? { code, params } : { code },
});

// ─── Abilities ───────────────────────────────────────────────────────────────

export interface OwnedAbility {
  seat: SeatId;
  source: SourceRef;
  ability: Ability;
  index: number;
  /** Usage key base (without round suffix). */
  key: string;
}

/** A cast spell is Under Review this round: its abilities (other than End Game scoring) are off. */
export const isSuspended = (s: GameState, card: CardId): boolean => (s.suspended?.[card] ?? 0) >= s.round;

/** Abilities active for a seat, in "entered play" order: identity first, then tableau in cast order. */
export function seatAbilities(s: GameState, c: ContentIndex, seat: SeatId): OwnedAbility[] {
  const out: OwnedAbility[] = [];
  const st = s.seats[seat]!;
  if (st.identity) {
    const def = c.identities.get(st.identity);
    def?.abilities.forEach((ability, index) =>
      out.push({ seat, source: { kind: 'identity', def: def.id }, ability, index, key: `${seat}:identity:${index}` }),
    );
  }
  for (const card of st.tableau) {
    const def = spellDef(c, s.cards[card]!);
    const review = isSuspended(s, card);
    def.abilities.forEach((ability, index) => {
      if (review && ability.kind !== 'endGame') return;
      out.push({ seat, source: { kind: 'card', card, def: def.id }, ability, index, key: `${seat}:${card}:${index}` });
    });
  }
  return out;
}

// ─── Decrees (global rules) ──────────────────────────────────────────────────

export const decreesInForce = (s: GameState): DecreeInForce[] => s.decrees ?? [];

/** Every global modifier of a type currently in force, with the Decree that carries it. */
export function globalMods<T extends GlobalMod['type']>(
  s: GameState,
  c: ContentIndex,
  type: T,
): { mod: Extract<GlobalMod, { type: T }>; decree: DecreeInForce }[] {
  const out: { mod: Extract<GlobalMod, { type: T }>; decree: DecreeInForce }[] = [];
  for (const d of decreesInForce(s)) {
    for (const mod of decreeDef(c, d.decree).effects) {
      if (mod.type === type) out.push({ mod: mod as Extract<GlobalMod, { type: T }>, decree: d });
    }
  }
  return out;
}

const globalDelta = (s: GameState, c: ContentIndex, type: 'archiveDraw' | 'planningPlays' | 'capacity' | 'problemVp' | 'handLimit' | 'arcaniumPrice'): number =>
  globalMods(s, c, type).reduce((n, m) => n + m.mod.delta, 0);

// ─── Resolutions (P-17) and Annex rooms (P-18) ──────────────────────────────

export const resolutionsInForce = (s: GameState): ResolutionEntry[] => s.resolutions?.inForce ?? [];

/** Every Resolution modifier of a type in force, with the Resolution that carries it. */
export function resolutionMods<T extends ResolutionMod['type']>(
  s: GameState,
  c: ContentIndex,
  type: T,
): { mod: Extract<ResolutionMod, { type: T }>; entry: ResolutionEntry }[] {
  const out: { mod: Extract<ResolutionMod, { type: T }>; entry: ResolutionEntry }[] = [];
  for (const entry of resolutionsInForce(s)) {
    for (const mod of resolutionDef(c, entry.resolution).modifiers) {
      if (mod.type === type) out.push({ mod: mod as Extract<ResolutionMod, { type: T }>, entry });
    }
  }
  return out;
}

export const isResolutionInForce = (s: GameState, id: string): boolean => resolutionsInForce(s).some((e) => e.resolution === id);

/** Currency Reform: mana per permit when a permit cost or fee may be paid with mana, else null. */
export function manaPerPermit(s: GameState, c: ContentIndex): number | null {
  const mods = resolutionMods(s, c, 'substitutePayment');
  return mods.length ? Math.min(...mods.map((m) => m.mod.rate)) : null;
}

/** Free Action cards a seat may still play this round (Memos Fly Free), and how many in all. */
export function freeActionPlays(s: GameState, c: ContentIndex, seat: SeatId): { allowed: number; used: number } {
  const allowed = resolutionMods(s, c, 'freeActionCard').reduce((n, m) => n + m.mod.perRound, 0);
  return { allowed, used: s.usage[memoKey(s, seat)] ?? 0 };
}
export const memoKey = (s: GameState, seat: SeatId): string => usageKey(s, `memo:${seat}`, 'round');

/** Is a Decree barred by a Resolution in force (Free Roads)? Returns the Resolution, or null. */
export function decreeBan(s: GameState, c: ContentIndex, decree: DefId): DefId | null {
  const effects = decreeDef(c, decree).effects;
  for (const { mod, entry } of resolutionMods(s, c, 'banDecrees')) {
    if (effects.some((e) => e.type === mod.modifier)) return entry.resolution;
  }
  return null;
}

const ANNEX = new Set<string>(ANNEX_IDS);
export const isAnnex = (location: LocationId): boolean => ANNEX.has(location);

/** The Annex bay an Annex room is open in, or -1. */
export const annexBayOf = (s: GameState, location: LocationId): number => s.annex?.bays.indexOf(location) ?? -1;

export function planningPlays(s: GameState, c: ContentIndex): number {
  return Math.max(0, s.meta.options.planningPlays + globalDelta(s, c, 'planningPlays'));
}

/** Rivals cannot Suspend or Revoke this seat's spells, or Repeal its Decrees. */
export const isAppealImmune = (s: GameState, c: ContentIndex, seat: SeatId): boolean =>
  modifiersOf(s, c, seat, 'appealImmune').length > 0;

/**
 * Spells an appeal by `seat` may target: rivals' cast spells. Under Review is temporary and may hit
 * anyone; a Revoke only reaches rivals with at least as many VP as you (no kicking the trailing player).
 */
export function appealTargets(s: GameState, c: ContentIndex, seat: SeatId, purpose: 'suspend' | 'revoke'): { card: CardId; seat: SeatId }[] {
  const me = s.seats[seat]!;
  const out: { card: CardId; seat: SeatId }[] = [];
  for (const o of s.seats) {
    if (o.seat === seat || isAppealImmune(s, c, o.seat)) continue;
    if (purpose === 'revoke' && o.vp < me.vp) continue;
    for (const card of o.tableau) {
      if (purpose === 'suspend') {
        if (isSuspended(s, card)) continue;
        const def = spellDef(c, s.cards[card]!);
        if (!def.abilities.some((a) => a.kind === 'trigger' || a.kind === 'activated' || a.kind === 'modifier')) continue;
      }
      out.push({ card, seat: o.seat });
    }
  }
  return out;
}

/** Decrees `seat` may repeal: any in force, except those authored by an immune rival. */
export function repealable(s: GameState, c: ContentIndex, seat: SeatId): DefId[] {
  return decreesInForce(s)
    .filter((d) => d.by === null || d.by === seat || !isAppealImmune(s, c, d.by))
    .map((d) => d.decree);
}

export function usageKey(s: GameState, base: string, limit: Limit): string {
  return limit === 'round' ? `${base}@r${s.round}` : `${base}@game`;
}

export function isUnused(s: GameState, base: string, limit: Limit | undefined): boolean {
  return !limit || !s.usage[usageKey(s, base, limit)];
}

export function markUsed(s: GameState, base: string, limit: Limit | undefined): void {
  if (limit) s.usage[usageKey(s, base, limit)] = 1;
}

export function modifiersOf<T extends Modifier['type']>(
  s: GameState,
  c: ContentIndex,
  seat: SeatId,
  type: T,
): { mod: Extract<Modifier, { type: T }>; owned: OwnedAbility }[] {
  const out: { mod: Extract<Modifier, { type: T }>; owned: OwnedAbility }[] = [];
  for (const owned of seatAbilities(s, c, seat)) {
    if (owned.ability.kind === 'modifier' && owned.ability.mod.type === type) {
      out.push({ mod: owned.ability.mod as Extract<Modifier, { type: T }>, owned });
    }
  }
  return out;
}

/** First unused once-per-round/-game modifier of a type, if any. */
function unusedLimitedMod(s: GameState, c: ContentIndex, seat: SeatId, type: Modifier['type'], limit: Limit) {
  return modifiersOf(s, c, seat, type).find((m) => isUnused(s, m.owned.key, limit));
}

export function actionHandLimit(s: GameState, c: ContentIndex, seat: SeatId): number {
  const base = modifiersOf(s, c, seat, 'handLimit').reduce((max, m) => Math.max(max, m.mod.value), s.meta.options.actionHandLimit);
  return Math.max(1, base + globalDelta(s, c, 'handLimit'));
}

export function archiveDrawCount(s: GameState, c: ContentIndex, seat: SeatId): number {
  const own = modifiersOf(s, c, seat, 'archiveDraw').reduce((n, m) => n + m.mod.delta, s.meta.options.archiveDraw);
  return Math.max(0, own + globalDelta(s, c, 'archiveDraw'));
}

// ─── Assistants & placement ──────────────────────────────────────────────────

export const capacity = (s: GameState, c?: ContentIndex): number =>
  (s.meta.options.capacity[String(s.meta.playerCount)] ?? 2) + (c ? globalDelta(s, c, 'capacity') : 0);

/** A location's capacity: its own fixed one (the Office of Precedence), else the table's. */
export const locationCapacity = (s: GameState, c: ContentIndex, location: LocationId): number =>
  locationDef(c, location).capacity ?? capacity(s, c);

/**
 * Is this location in play right now? v1 has no Office of Precedence; an Annex room (P-18) is only
 * while it is open in an Annex bay.
 */
export function locationOpen(s: GameState, location: LocationId): boolean {
  if (location === 'precedence') return s.meta.options.precedenceOffice;
  if (ANNEX.has(location)) return annexBayOf(s, location) >= 0;
  return true;
}

/** The round the Interns arrive (P-12; Labour Accord may bring it forward), or null. */
export const internRoundOf = (s: GameState): number | null =>
  s.internRound !== undefined ? s.internRound : (s.meta.options.internRound ?? null);

/** The last round of the game (Early Closing may bring it forward). */
export const lastRoundOf = (s: GameState): number => s.lastRound ?? s.meta.options.rounds;

/** The most Assistants `seat` may have right now (temporary ones count), or Infinity without a cap. */
export function assistantCap(s: GameState): number {
  const o = s.meta.options;
  if (o.assistantCap === null || o.assistantCap === undefined) return Infinity;
  const intern = internRoundOf(s);
  return o.assistantCap + (intern && s.round >= intern ? 1 : 0);
}

export const hasAvailablePermanent = (s: GameState, seat: SeatId): boolean =>
  s.seats[seat]!.assistants.some((a) => !a.temp && !a.placed);

/** The assistant a placement this turn would use, respecting "one base placement per turn". */
export function nextAssistant(s: GameState, seat: SeatId): Assistant | null {
  const st = s.seats[seat]!;
  if (!s.turn || s.turn.seat !== seat) return null;
  if (!s.turn.basePlaced) {
    const permanent = st.assistants.find((a) => !a.temp && !a.placed);
    if (permanent) return permanent;
  }
  return st.assistants.find((a) => a.temp && !a.placed) ?? null;
}

export type PlacementCheck = { ok: true; ignoreCapacityKey?: string } | { ok: false; error: RuleError };

export function placementCheck(s: GameState, c: ContentIndex, seat: SeatId, location: LocationId): PlacementCheck {
  const loc = s.board[location];
  if (!loc) return err('UNKNOWN_LOCATION', { location });
  if (!locationOpen(s, location)) return err('LOCATION_CLOSED', { location });
  // P-19: the Stamp's holder can't take it again (no Stamp lock).
  if (location === 'precedence' && s.meta.options.precedenceHolderBan && (s.stampHolder ?? s.firstSeat) === seat) return err('HOLDS_STAMP', { location });
  const blocker = loc.blockedBy.find((b) => b !== seat);
  if (blocker !== undefined) return err('LOCATION_BLOCKED', { location, by: blocker });
  const cap = locationCapacity(s, c, location);
  if (loc.occupants.length >= cap) {
    // A fixed capacity is absolute: only one player can take the Stamp of Precedence each round.
    if (locationDef(c, location).capacity !== undefined) return err('LOCATION_FULL', { location, capacity: cap });
    const harlan = unusedLimitedMod(s, c, seat, 'ignoreCapacity', 'round');
    if (harlan) return { ok: true, ignoreCapacityKey: harlan.owned.key };
    return err('LOCATION_FULL', { location, capacity: cap });
  }
  return { ok: true };
}

export function legalLocations(s: GameState, c: ContentIndex, seat: SeatId): LocationId[] {
  return (Object.keys(s.board) as LocationId[]).filter((l) => placementCheck(s, c, seat, l).ok);
}

export const assistantOwner = (assistantId: string): SeatId => Number(assistantId.slice(1, assistantId.indexOf('a')));

// ─── Spell costs & payments ──────────────────────────────────────────────────

export function effectiveSpellCost(s: GameState, c: ContentIndex, seat: SeatId, def: DefId): Permits {
  const values = spellValues(c, def, s.meta.options);
  const school = spellDef(c, def).school;
  const cost = toPermits(values.cost);
  for (const { mod } of modifiersOf(s, c, seat, 'spellCost')) {
    if (mod.school !== school) continue;
    if (cost[mod.color] > mod.min) cost[mod.color] = Math.max(mod.min, cost[mod.color] + mod.delta);
  }
  const own = SCHOOL_COLOR[school];
  for (const { mod } of globalMods(s, c, 'schoolCost')) {
    if (mod.delta < 0 ? cost[own] > mod.min : cost[own] > 0) cost[own] = Math.max(mod.delta < 0 ? mod.min : 0, cost[own] + mod.delta);
  }
  return cost;
}

export function arcaniumPrice(s: GameState, c: ContentIndex, seat: SeatId, def: DefId, opDiscount = 0): number {
  let price = spellValues(c, def, s.meta.options).price - opDiscount;
  for (const { mod } of modifiersOf(s, c, seat, 'arcaniumPrice')) price += mod.delta;
  price += globalDelta(s, c, 'arcaniumPrice');
  for (const m of s.modifiers) if (m.seat === seat && m.kind === 'nextPurchaseDiscount') price -= m.amount;
  return Math.max(0, price);
}

export interface ResolvedPayment {
  spend: Permits;
  mana: number;
  /** Usage keys (already suffixed) consumed by this payment. */
  uses: string[];
  consumesPrecedent: boolean;
  /** The Stamp Pound's reduction is used up by this cast. */
  consumesPound: boolean;
}

/** A well-formed permit bag: only colour keys, whole non-negative counts. */
export function isBag(x: unknown): x is PermitBag {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
  return Object.entries(x).every(([k, v]) => (COLORS as readonly string[]).includes(k) && typeof v === 'number' && Number.isInteger(v) && v >= 0);
}

export function validatePayment(
  s: GameState,
  c: ContentIndex,
  seat: SeatId,
  card: CardId,
  payment: Payment,
): Result<ResolvedPayment> {
  const st = s.seats[seat]!;
  if (!st.spellHand.includes(card)) return err('NOT_IN_HAND', { card });
  const def = s.cards[card]!;
  const remaining = effectiveSpellCost(s, c, seat, def);
  const uses: string[] = [];
  const precedent = s.modifiers.find((m) => m.seat === seat && m.kind === 'nextSpellDiscount');
  const pound = s.modifiers.find((m) => m.seat === seat && m.kind === 'nextSpellPermits');
  for (const bag of [payment.reduceBy, payment.manaPermits]) if (bag !== undefined && !isBag(bag)) return err('INVALID_PAYMENT', { reason: 'bag' });

  if (payment.free) {
    const archivox = unusedLimitedMod(s, c, seat, 'freeCast', 'game');
    if (!archivox) return err('INVALID_PAYMENT', { reason: 'freeCast' });
    uses.push(usageKey(s, archivox.owned.key, 'game'));
    for (const col of COLORS) remaining[col] = 0;
    if (payment.discountOn || payment.yellowAs || payment.manaFor || permitTotal(payment.reduceBy ?? {}) || permitTotal(payment.manaPermits ?? {})) {
      return err('INVALID_PAYMENT', { reason: 'free' });
    }
    return { ok: true, value: { spend: emptyPermits(), mana: 0, uses, consumesPrecedent: !!precedent, consumesPound: !!pound } };
  }

  // Layer 3: one-shot reductions — Legal Precedent (1 permit), then the Stamp Pound (2 of your choice).
  const total = permitTotal(remaining);
  if (precedent && total > 0) {
    const col = payment.discountOn;
    if (!col || remaining[col] <= 0) return err('INVALID_PAYMENT', { reason: 'discountOn' });
    remaining[col] -= precedent.amount;
    if (remaining[col] < 0) remaining[col] = 0;
  } else if (payment.discountOn) {
    return err('INVALID_PAYMENT', { reason: 'noDiscount' });
  }
  const left = permitTotal(remaining);
  if (pound && left > 0) {
    const bag = payment.reduceBy ?? {};
    if (permitTotal(bag) !== Math.min(pound.amount, left) || COLORS.some((col) => (bag[col] ?? 0) > remaining[col])) {
      return err('INVALID_PAYMENT', { reason: 'reduceBy' });
    }
    for (const col of COLORS) remaining[col] -= bag[col] ?? 0;
  } else if (permitTotal(payment.reduceBy ?? {}) > 0) {
    return err('INVALID_PAYMENT', { reason: 'noReduction' });
  }

  // Layer 4: substitutions.
  let extraYellow = 0;
  if (payment.yellowAs) {
    const alch = unusedLimitedMod(s, c, seat, 'substitute', 'round');
    if (!alch || payment.yellowAs === 'yellow' || remaining[payment.yellowAs] <= 0) {
      return err('INVALID_PAYMENT', { reason: 'yellowAs' });
    }
    uses.push(usageKey(s, alch.owned.key, 'round'));
    remaining[payment.yellowAs] -= 1;
    extraYellow = 1;
  }

  let mana = 0;
  if (payment.manaFor) {
    const margot = unusedLimitedMod(s, c, seat, 'manaForPermit', 'round');
    if (!margot || remaining[payment.manaFor] <= 0) return err('INVALID_PAYMENT', { reason: 'manaFor' });
    uses.push(usageKey(s, margot.owned.key, 'round'));
    remaining[payment.manaFor] -= 1;
    mana = 1;
  }

  // Currency Reform (P-17): any of the permits still owed, paid with mana instead.
  const byMana = permitTotal(payment.manaPermits ?? {});
  if (byMana > 0) {
    const rate = manaPerPermit(s, c);
    if (rate === null) return err('INVALID_PAYMENT', { reason: 'manaPermits' });
    for (const col of COLORS) {
      const n = payment.manaPermits![col] ?? 0;
      if (n > remaining[col]) return err('INVALID_PAYMENT', { reason: 'manaPermits' });
      remaining[col] -= n;
    }
    mana += byMana * rate;
  }

  const spend = { ...remaining, yellow: remaining.yellow + extraYellow };
  for (const col of COLORS) {
    if (st.permits[col] < spend[col]) {
      return err('INSUFFICIENT_PERMITS', { color: col, missing: spend[col] - st.permits[col] });
    }
  }
  if (st.mana < mana) return err('INSUFFICIENT_MANA', { missing: mana - st.mana });
  return { ok: true, value: { spend, mana, uses, consumesPrecedent: !!precedent, consumesPound: !!pound } };
}

/** Score used to rank payments: keep scarce (red/yellow) permits, mana, and once-per-round abilities. */
function paymentScore(p: ResolvedPayment, payment: Payment): number {
  return (
    p.spend.red * 2 + p.spend.yellow * 2 + p.spend.blue + p.spend.green + p.mana * 1.5 +
    (payment.yellowAs ? 0.4 : 0) + (payment.manaFor ? 0.4 : 0) + (payment.free ? 50 : 0)
  );
}

/** Every way to take `n` permits out of `cost` (multisets bounded by it), each as a bag. */
function subBags(cost: Permits, n: number): PermitBag[] {
  const out: PermitBag[] = [];
  const rec = (i: number, left: number, acc: PermitBag) => {
    if (left === 0) return void out.push({ ...acc });
    if (i >= COLORS.length) return;
    const col = COLORS[i]!;
    for (let k = Math.min(left, cost[col]); k >= 0; k--) rec(i + 1, left - k, k ? { ...acc, [col]: k } : acc);
  };
  rec(0, n, {});
  return out;
}

/** The permits a seat is short of for `spend`, capped by what may still be paid with mana. */
function shortfall(have: Permits, spend: Permits, cap: Permits): PermitBag {
  const out: PermitBag = {};
  for (const col of COLORS) {
    const n = Math.min(cap[col], Math.max(0, spend[col] - have[col]));
    if (n > 0) out[col] = n;
  }
  return out;
}

/**
 * Every valid payment for a spell in hand, best (cheapest to keep) first. Under Currency Reform the
 * mana variant pays with mana only what the permits don't cover (the client can offer more).
 */
export function enumeratePayments(s: GameState, c: ContentIndex, seat: SeatId, card: CardId): Payment[] {
  const st = s.seats[seat]!;
  if (!st.spellHand.includes(card)) return [];
  const cost = effectiveSpellCost(s, c, seat, s.cards[card]!);
  const precedent = s.modifiers.find((m) => m.seat === seat && m.kind === 'nextSpellDiscount');
  const pound = s.modifiers.find((m) => m.seat === seat && m.kind === 'nextSpellPermits');
  const costColors = COLORS.filter((col) => cost[col] > 0);
  const discounts: (Color | undefined)[] = precedent && costColors.length ? costColors : [undefined];
  const hasAlch = !!unusedLimitedMod(s, c, seat, 'substitute', 'round');
  const hasMargot = !!unusedLimitedMod(s, c, seat, 'manaForPermit', 'round');
  const hasFree = !!unusedLimitedMod(s, c, seat, 'freeCast', 'game');
  const reform = manaPerPermit(s, c) !== null;

  const candidates: Payment[] = [];
  for (const discountOn of discounts) {
    // What the Stamp Pound may remove depends on what Legal Precedent left.
    const left = { ...cost };
    if (precedent && discountOn) left[discountOn] = Math.max(0, left[discountOn] - precedent.amount);
    const leftTotal = permitTotal(left);
    const reductions: (PermitBag | undefined)[] = pound && leftTotal > 0 ? subBags(left, Math.min(pound.amount, leftTotal)) : [undefined];
    for (const reduceBy of reductions) {
      for (const yellowAs of hasAlch ? [undefined, ...COLORS.filter((x) => x !== 'yellow')] : [undefined]) {
        for (const manaFor of hasMargot ? [undefined, ...COLORS] : [undefined]) {
          const p: Payment = {};
          if (discountOn) p.discountOn = discountOn;
          if (reduceBy) p.reduceBy = reduceBy;
          if (yellowAs) p.yellowAs = yellowAs;
          if (manaFor) p.manaFor = manaFor;
          candidates.push(p);
          if (reform) {
            // The permits still owed after everything else, less what this seat holds.
            const owed = { ...left };
            for (const col of COLORS) owed[col] -= reduceBy?.[col] ?? 0;
            if (yellowAs) owed[yellowAs] -= 1;
            if (manaFor) owed[manaFor] -= 1;
            const spend = { ...owed, yellow: owed.yellow + (yellowAs ? 1 : 0) };
            const short = shortfall(st.permits, spend, owed);
            if (permitTotal(short) > 0) candidates.push({ ...p, manaPermits: short });
          }
        }
      }
    }
  }
  if (hasFree) candidates.push({ free: true });

  const valid: { payment: Payment; score: number; key: string }[] = [];
  const seen = new Set<string>();
  for (const payment of candidates) {
    const r = validatePayment(s, c, seat, card, payment);
    if (!r.ok) continue;
    const key = JSON.stringify([r.value.spend, r.value.mana, payment.free ?? false, payment.yellowAs ?? '', payment.manaFor ?? '']);
    if (seen.has(key)) continue;
    seen.add(key);
    valid.push({ payment, score: paymentScore(r.value, payment), key });
  }
  valid.sort((a, b) => a.score - b.score || a.key.localeCompare(b.key));
  return valid.map((v) => v.payment);
}

// ─── Schools, problems, stats ────────────────────────────────────────────────

function tableauSchools(s: GameState, c: ContentIndex, seat: SeatId): { schools: School[]; wild: boolean }[] {
  return s.seats[seat]!.tableau.map((card) => {
    const def = spellDef(c, s.cards[card]!);
    const wild = def.abilities.some((a) => a.kind === 'modifier' && a.mod.type === 'countsAsAllSchools');
    return { schools: wild ? [...SCHOOLS] : [def.school], wild };
  });
}

/** Count of cast spells of a school (wild spells count for every school). */
export function schoolCount(s: GameState, c: ContentIndex, seat: SeatId, school: School, forRequirements = true): number {
  if (!forRequirements) {
    return s.seats[seat]!.tableau.filter((card) => spellDef(c, s.cards[card]!).school === school).length;
  }
  return tableauSchools(s, c, seat).filter((t) => t.schools.includes(school)).length;
}

/** Distinct schools coverable by different spells (wild spells fill any missing school). */
export function distinctSchools(s: GameState, c: ContentIndex, seat: SeatId, forRequirements = true): number {
  const list = forRequirements
    ? tableauSchools(s, c, seat)
    : s.seats[seat]!.tableau.map((card) => ({ schools: [spellDef(c, s.cards[card]!).school], wild: false }));
  const fixed = new Set(list.filter((t) => !t.wild).map((t) => t.schools[0]!));
  const wild = list.filter((t) => t.wild).length;
  return Math.min(SCHOOLS.length, fixed.size + wild);
}

export function statValue(s: GameState, c: ContentIndex, seat: SeatId, name: StatName): number {
  const st = s.seats[seat]!;
  const departments = [...c.locations.values()].filter((l) => l.keyword === 'department').map((l) => l.id);
  switch (name) {
    case 'sameDepartmentRounds':
      return Math.max(0, ...departments.map((d) => new Set(st.stats.visits[d] ?? []).size));
    case 'departmentsVisited':
      return departments.filter((d) => (st.stats.visits[d]?.length ?? 0) > 0).length;
    case 'maxSchoolCount':
      return Math.max(0, ...SCHOOLS.map((sc) => schoolCount(s, c, seat, sc, false)));
    case 'distinctSchools':
      return distinctSchools(s, c, seat, false);
    case 'problemsClaimed':
      return st.problems.length;
    case 'spellsCastByRound3':
      return st.stats.castRounds.filter((r) => r <= 3).length;
    case 'manaLeft':
      return st.mana;
    case 'permitsLeft':
      return permitTotal(st.permits);
    case 'actionCardsPlayed':
      return st.stats.actionCardsPlayed;
    case 'spellsBought':
      return st.stats.spellsBought;
    case 'maxSpellPriceCast':
      return st.stats.maxCastPrice;
    case 'conduitRounds':
      return new Set(st.stats.visits['mana-conduit'] ?? []).size;
    case 'auditorVisits':
      return st.stats.visits['grand-auditor']?.length ?? 0;
    case 'reached20BeforeRound7':
      return st.stats.reached20Round !== null && st.stats.reached20Round < 7 ? 1 : 0;
    case 'choicePermitsGained':
      return st.stats.choicePermitsGained;
    case 'spellsCast':
      return st.tableau.length;
    case 'totalPrintedVp':
      return st.tableau.reduce((n, card) => n + spellValues(c, s.cards[card]!, s.meta.options).vp, 0);
    case 'appealsFiled':
      return st.stats.appealsFiled ?? 0;
    case 'decreesEnacted':
      return st.stats.decreesEnacted ?? 0;
    case 'decreesInForce':
      return decreesInForce(s).filter((d) => d.by === seat).length;
    case 'manaCollected':
      return st.stats.manaCollected ?? 0;
    case 'sharedPlacements':
      return st.stats.sharedPlacements ?? 0;
  }
}

export function conditionMet(s: GameState, c: ContentIndex, seat: SeatId, cond: Condition): boolean {
  if ('all' in cond) return cond.all.every((x) => conditionMet(s, c, seat, x));
  const v = statValue(s, c, seat, cond.stat);
  if (cond.atLeast !== undefined && v < cond.atLeast) return false;
  if (cond.atMost !== undefined && v > cond.atMost) return false;
  return true;
}

export function requirementMet(s: GameState, c: ContentIndex, seat: SeatId, req: Requirement | undefined): boolean {
  if (!req) return true;
  const st = s.seats[seat]!;
  if ('castSchool' in req) return schoolCount(s, c, seat, req.castSchool.school) >= req.castSchool.atLeast;
  if ('distinctSchools' in req) return distinctSchools(s, c, seat) >= req.distinctSchools;
  if ('spellsCast' in req) return st.tableau.length >= req.spellsCast;
  if ('totalPrintedVp' in req) return statValue(s, c, seat, 'totalPrintedVp') >= req.totalPrintedVp;
  if ('stat' in req) return statValue(s, c, seat, req.stat.name) >= req.stat.atLeast;
  if ('permitsEach' in req) return COLORS.every((col) => st.permits[col] >= req.permitsEach);
  return false;
}

export function problemVpBonus(s: GameState, c: ContentIndex, seat: SeatId): number {
  return modifiersOf(s, c, seat, 'problemVp').reduce((n, m) => n + m.mod.delta, 0) + globalDelta(s, c, 'problemVp');
}

export function eligibleProblems(s: GameState, c: ContentIndex, seat: SeatId): ProblemOption[] {
  const st = s.seats[seat]!;
  const maxClaims = s.meta.options.problemTiers ? 2 : 1;
  const rate = manaPerPermit(s, c);
  const out: ProblemOption[] = [];
  for (const slot of s.zones.problems) {
    if (slot.claims.includes(seat) || slot.claims.length >= maxClaims) continue;
    const def = problemDef(c, slot.problem);
    if (!requirementMet(s, c, seat, def.requirement)) continue;
    let manaPermits: PermitBag | undefined;
    if (def.fee) {
      const fee = toPermits(def.fee.permits);
      // Currency Reform: the permits the fee is short of may be paid with mana instead.
      const short = shortfall(st.permits, fee, fee);
      const n = permitTotal(short);
      if (n > 0) {
        if (rate === null || st.mana < (def.fee.mana ?? 0) + n * rate) continue;
        manaPermits = short;
      } else if ((def.fee.mana ?? 0) > st.mana) continue;
    }
    const tier = (slot.claims.length === 0 ? 1 : 2) as 1 | 2;
    out.push({ problem: slot.problem, tier, vp: def.tiers[tier - 1] + problemVpBonus(s, c, seat), ...(manaPermits ? { manaPermits } : {}) });
  }
  return out;
}

/**
 * How a seat pays a Problem's fee with `manaPermits` of its permits paid in mana (Currency Reform):
 * the permits and mana spent, or null if that isn't a valid way to pay it.
 */
export function feePayment(s: GameState, c: ContentIndex, seat: SeatId, problem: DefId, manaPermits: PermitBag | undefined): { permits: PermitBag; mana: number } | null {
  const st = s.seats[seat]!;
  const fee = problemDef(c, problem).fee;
  const permits = toPermits(fee?.permits);
  let mana = fee?.mana ?? 0;
  const byMana = permitTotal(manaPermits ?? {});
  if (byMana > 0) {
    const rate = manaPerPermit(s, c);
    if (rate === null || !isBag(manaPermits)) return null;
    for (const col of COLORS) {
      const n = manaPermits[col] ?? 0;
      if (n > permits[col]) return null;
      permits[col] -= n;
    }
    mana += byMana * rate;
  }
  if (COLORS.some((col) => st.permits[col] < permits[col]) || st.mana < mana) return null;
  return { permits, mana };
}

export function rankFor(c: ContentIndex, vp: number): number {
  let rank = 0;
  c.pack.ranks.forEach((r, i) => {
    if (vp >= r.minVp) rank = i;
  });
  return rank;
}

export const isFree = (s: GameState): boolean => s.stack.length === 0 && s.pending.length === 0;
